import { useCallback, useEffect, useRef } from 'react'
import type { JSX } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { parseAsString, useQueryState } from 'nuqs'

import { usePostComposer } from '~publish/hooks/usePostComposer'

import { useOnPostComposerClose } from './helpers'

export function PostCreationDialog(): JSX.Element | null {
  const { id: channelId = 'all' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const hasOpenedComposerRef = useRef(false)

  const { createNewPostInComposer, isOpen } = usePostComposer()
  const [textParam] = useQueryState('text', parseAsString)
  const text = textParam ?? undefined
  const [prefillDraftData] = useQueryState('prefillDraftData', parseAsString)

  useEffect(() => {
    if (hasOpenedComposerRef.current) return

    // An app-shell flow can open the composer before this route effect runs.
    // A route remount resets `hasOpenedComposerRef`, so do not replace an open
    // composer with route data.
    if (isOpen) {
      hasOpenedComposerRef.current = true
      return
    }

    // When `prefillDraftData=true` is in the URL we're re-entering the composer
    // after a channel-connect OAuth round-trip; surface the localStorage prefill
    // so this synchronous dispatch already carries the saved draft and doesn't
    // race app-shell's ModalManager (which fires the same flag on its own
    // schedule, gated by connectedChannels).
    hasOpenedComposerRef.current = true
    void createNewPostInComposer(
      {
        channels: channelId === 'all' ? channelId : [channelId],
        prefillPostData: { text },
      },
      prefillDraftData === 'true'
        ? { prefillFromLocalStorage: true }
        : undefined,
    )
    // We only want to open the composer once on mount, not when createNewPostInComposer changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId, text, prefillDraftData, isOpen])

  const handleComposerClose = useCallback(() => {
    navigate('..')
  }, [navigate])

  useOnPostComposerClose(handleComposerClose)

  return null
}
